<script setup lang="ts">
defineEmits<{
  submit: []
}>()
const formPreferences = defineModel<SearchPreferenceForm>({ required: true })
const vehicleSmokingDisallowed = defineModel<boolean>('vehicleSmokingDisallowed', { required: true })

export interface AppSearchFilterPreferencesProps {
  isCrossedSearch?: boolean
}
withDefaults(
  defineProps<AppSearchFilterPreferencesProps>(),
  {
    isCrossedSearch: false,
  },
)

const { licenseOptions, ownerOptions, reviewAverageOptions } = useSearchFiltersOptions()
</script>

<template>
  <form
    id="search-form-preferences"
    class="max-h-[calc(100vh-15rem)] w-96 overflow-y-auto"
    @submit.prevent="$emit('submit')"
  >
    <AppSearchFiltersInstantBookingOption
      v-if="!isCrossedSearch"
      v-model="formPreferences.instant_booking"
    />
    <AppSearchFilterFormSection :title="$t('components.app_search.filters.travel')">
      <YscInputCheckbox
        id="km_unlimited"
        v-model="formPreferences.km_unlimited"
        name="km_unlimited"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsMileage
            class="h-5 w-5"
            aria-hidden="true"
          />
          {{ $t('components.app_search.filters.km_unlimited') }}
        </div>
      </YscInputCheckbox>
      <YscInputCheckbox
        id="international"
        v-model="formPreferences.international"
        name="international"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsInternational
            aria-hidden="true"
            class="h-5 w-5"
          />
          {{ $t('components.app_search.filters.international') }}
        </div>
      </YscInputCheckbox>
      <YscInputCheckbox
        id="pets"
        v-model="formPreferences.pets"
        name="pets"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsPetsAllowed
            aria-hidden="true"
            class="h-5 w-5"
          />{{ $t('components.app_search.filters.pets') }}
        </div>
      </YscInputCheckbox>
    </AppSearchFilterFormSection>
    <AppSearchFilterFormSection :title="$t('commons.vehicle')">
      <YscInputCheckbox
        v-if="!isCrossedSearch"
        id="vehicle_height"
        v-model="formPreferences.vehicle_height"
        name="vehicle_height"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsRoadSignTwoMetersHeight
            aria-hidden="true"
            class="h-5 w-5"
          />

          {{ $t('components.app_search.filters.vehicle_height') }}
        </div>
      </YscInputCheckbox>
      <YscInputCheckbox
        id="less_than_five"
        v-model="formPreferences.less_than_five"
        name="less_than_five"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsBirthdayCake
            aria-hidden="true"
            class="h-5 w-5"
          />

          {{ $t('components.app_search.filters.less_than_five') }}
        </div>
      </YscInputCheckbox>
      <YscInputCheckbox
        v-if="!isCrossedSearch"
        id="disabled_access"
        v-model="formPreferences.disabled_access"
        name="disabled_access"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsWheelchair
            aria-hidden="true"
            class="h-5 w-5"
          />
          {{ $t('components.app_search.filters.disabled_access') }}
        </div>
      </YscInputCheckbox>
      <YscInputCheckbox
        id="smoking_disabllowed"
        v-model="vehicleSmokingDisallowed"
        name="smoking_disabllowed"
        is-block
        class="flex-row-reverse justify-between !space-x-0 w-full"
      >
        <div class="flex flex-row gap-3 items-center">
          <YscIconsSmokingNotAllowed
            aria-hidden="true"
            class="h-5 w-5"
          />{{ $t('components.app_search.filters.smoking_not_allowed') }}
        </div>
      </YscInputCheckbox>
    </AppSearchFilterFormSection>
    <section class="border-t border-gray-200 px-4 pt-6 pb-8 flex flex-col gap-8">
      <div
        v-if="!isCrossedSearch"
        class="flex flex-col gap-3"
      >
        <span class="block text-base font-semibold">
          {{ $t('components.app_search.filters.minimal_review') }}
        </span>
        <YscToggleGroup
          v-model.number="formPreferences.review_average__gte"
          class="text-sm w-full"
          name="review"
          grow
          :options="reviewAverageOptions"
        />
      </div>
      <div class="flex flex-col gap-3">
        <span class="block text-base font-semibold">
          {{ $t('components.app_search.filters.owner') }}
        </span>
        <YscToggleGroup
          v-if="!isCrossedSearch"
          v-model.number="formPreferences.owner_types"
          class="text-sm"
          name="owner"
          grow
          :options="ownerOptions"
        />
        <YscInputCheckbox
          id="is_ambassador"
          v-model="formPreferences.is_ambassador"
          name="is_ambassador"
          is-block
          class="flex-row-reverse justify-between !space-x-0 w-full"
        >
          {{ $t('components.app_search.filters.is_ambassador') }}
        </YscInputCheckbox>
      </div>
      <div
        v-if="!isCrossedSearch"
        class="flex flex-col gap-3"
      >
        <span class="inline text-base font-semibold">
          {{ $t('components.app_search.filters.driver_license') }}
        </span>
        <YscToggleGroup
          v-model.number="formPreferences.licence_type"
          class="text-sm"
          name="licence"
          grow
          :options="licenseOptions"
        />
      </div>
    </section>
  </form>
</template>
